import * as Sentry from '@sentry/react';
import { useEffect } from 'react';

// Initialize Sentry for React error tracking
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.warn('Sentry DSN not configured. Error tracking is disabled.');
    return;
  }

  Sentry.init({
    dsn,
    environment:
      import.meta.env.VITE_SENTRY_ENVIRONMENT || (import.meta.env.PROD ? 'production' : 'development'),
    integrations: [
      // Captures errors in React components
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in production, 100% in development
    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions will be recorded
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors will be recorded

    // Release tracking
    release: import.meta.env.VITE_APP_VERSION || 'unknown',

    // Filter out local development errors if needed
    beforeSend(event: Sentry.ErrorEvent, hint: Sentry.EventHint) {
      // Filter out certain errors in development
      if (import.meta.env.DEV) {
        // Ignore ResizeObserver errors which are common and usually harmless
        if ((hint.originalException as Error)?.message?.includes('ResizeObserver')) {
          return null;
        }
        // Ignore network errors in development
        if ((hint.originalException as Error)?.message?.includes('NetworkError')) {
          return null;
        }
      }

      // Add user context if available
      const user = localStorage.getItem('user');
      if (user) {
        try {
          const userData = JSON.parse(user);
          Sentry.setUser({
            id: userData.id,
            email: userData.email,
            username: userData.name,
          });
        } catch (e) {
          // Ignore JSON parse errors
        }
      }

      return event;
    },

    // Ignore certain errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      // Random plugins/extensions
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      // Facebook related errors
      'fb_xd_fragment',
      // Network errors that are usually temporary
      /Failed to fetch/,
      /NetworkError/,
      /Load failed/,
    ],
  });
}

// Custom error boundary component
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Utility to capture custom errors
export const captureError = (error: Error, context?: Record<string, any>) => {
  console.error('Captured error:', error);
  Sentry.captureException(error, {
    contexts: {
      custom: context || {},
    },
  });
};

// Utility to capture messages
export const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info') => {
  Sentry.captureMessage(message, level);
};

// Utility to add breadcrumbs
export const addBreadcrumb = (breadcrumb: Sentry.Breadcrumb) => {
  Sentry.addBreadcrumb(breadcrumb);
};

// Utility to set user context
export const setUserContext = (user: { id: string; email?: string; username?: string }) => {
  Sentry.setUser(user);
};

// Utility to clear user context (on logout)
export const clearUserContext = () => {
  Sentry.setUser(null);
};

// Performance monitoring utilities
export const startTransaction = (name: string, op: string = 'navigation') => {
  return Sentry.startSpan({ name, op }, () => {});
};

// Profile a specific function
export const profileFunction = async <T>(name: string, fn: () => Promise<T>): Promise<T> => {
  const transaction = startTransaction(name, 'function');
  try {
    const result = await fn();
    transaction.setStatus('ok');
    return result;
  } catch (error) {
    transaction.setStatus('internal_error');
    throw error;
  } finally {
    transaction.finish();
  }
};
